import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Divider,
    TextField,
    CircularProgress,
    Fade,
    Avatar,
    Card,
    CardContent
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SummarizeIcon from '@mui/icons-material/Summarize';
import PersonIcon from '@mui/icons-material/Person';
import MailIcon from '@mui/icons-material/Mail';
import api from '../../services/api';
import { User } from '../../types/auth';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';

const ReportConfig = () => {
    const { learnerId } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const [learner, setLearner] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [sending, setSending] = useState(false);
    const { showToast } = useToast();

    const [options, setOptions] = useState({
        summary: true,
        connections: true,
        quizzes: true,
        progress: true,
        evaluations: true,
        emails: true,
        satisfaction: true,
        errors: true,
        chat: false // "à venir"
    });

    const [recipients, setRecipients] = useState({
        learner: false,
        manager: true, // Default checked as requested
        other: ''
    });

    const [showOtherInput, setShowOtherInput] = useState(false);

    useEffect(() => {
        fetchLearner();
    }, [learnerId]);

    const fetchLearner = async () => {
        try {
            const res = await api.get(`/api/users/${learnerId}`);
            setLearner(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching learner", err);
            setLoading(false);
            showToast('Impossible de charger les informations de l\'apprenant.', 'error');
        }
    };

    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOptions({
            ...options,
            [event.target.name]: event.target.checked,
        });
    };

    const handleRecipientChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        if (name === 'otherCheck') {
            setShowOtherInput(checked);
            // If unchecked, clear the text
            if (!checked) setRecipients({ ...recipients, other: '' });
        } else {
            setRecipients({
                ...recipients,
                [name]: checked,
            });
        }
    };

    const handleOtherEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRecipients({
            ...recipients,
            other: event.target.value,
        });
    };

    const handleGeneratePDF = async () => {
        setGenerating(true);
        try {
            const response = await api.post('/api/reports/generate', {
                learnerId,
                options
            }, {
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Rapport_${learner?.lastName}_${learner?.firstName}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);

            setGenerating(false);
            showToast('PDF généré avec succès.', 'success');
        } catch (err) {
            console.error("Error generating PDF", err);
            setGenerating(false);
            showToast('Erreur lors de la génération du PDF.', 'error');
        }
    };

    const handleGenerateAndSend = async () => {
        if (!recipients.learner && !recipients.manager && !recipients.other) {
            showToast('Veuillez sélectionner au moins un destinataire.', 'warning');
            return;
        }

        if (showOtherInput && !recipients.other) {
            showToast('Veuillez renseigner l\'email du tiers ou décocher la case.', 'warning');
            return;
        }

        setSending(true);
        try {
            await api.post('/api/reports/send', {
                learnerId,
                options,
                recipients: {
                    learner: recipients.learner,
                    manager: recipients.manager,
                    other: showOtherInput ? recipients.other : null
                }
            });
            setSending(false);
            showToast('Rapport généré et envoyé par email avec succès.', 'success');
        } catch (err) {
            console.error("Error sending report", err);
            setSending(false);
            showToast('Erreur lors de l\'envoi du rapport.', 'error');
        }
    };

    if (loading) return <div>Chargement...</div>;
    if (!learner) return <div>Apprenant non trouvé.</div>;

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/reports')} // Assuming this is the list page
                sx={{ mb: 3, color: '#667eea', fontWeight: 600 }}
            >
                Retour
            </Button>

            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 4 },
                    mb: 4,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 10px 40px -10px rgba(102, 126, 234, 0.5)'
                }}
            >
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                <Box sx={{ position: 'absolute', bottom: -30, right: 100, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64, backdropFilter: 'blur(10px)' }}>
                        <SummarizeIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                            Générateur de Rapport
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            {learner.firstName} {learner.lastName}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                {/* Configuration Card */}
                <Box sx={{ flex: { xs: '1 1 100%', md: '0 1 66.666%' } }}>
                    <Fade in timeout={500}>
                        <Card sx={{ borderRadius: 4, overflow: 'visible', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SummarizeIcon color="primary" /> Contenu du rapport
                                </Typography>

                                <FormGroup>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                        <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                                            <FormControlLabel control={<Checkbox checked={options.summary} onChange={handleOptionChange} name="summary" />} label="Récapitulatif Global" />
                                        </Box>
                                        <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                                            <FormControlLabel control={<Checkbox checked={options.connections} onChange={handleOptionChange} name="connections" />} label="Sessions de Connexion" />
                                        </Box>
                                        <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                                            <FormControlLabel control={<Checkbox checked={options.quizzes} onChange={handleOptionChange} name="quizzes" />} label="Tests et Quiz" />
                                        </Box>
                                        <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                                            <FormControlLabel control={<Checkbox checked={options.progress} onChange={handleOptionChange} name="progress" />} label="Progression des Cours" />
                                        </Box>
                                        <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                                            <FormControlLabel control={<Checkbox checked={options.evaluations} onChange={handleOptionChange} name="evaluations" />} label="Evaluations et Corrections" />
                                        </Box>
                                        <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                                            <FormControlLabel control={<Checkbox checked={options.emails} onChange={handleOptionChange} name="emails" />} label="Historique des Emails" />
                                        </Box>
                                        <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                                            <FormControlLabel control={<Checkbox checked={options.satisfaction} onChange={handleOptionChange} name="satisfaction" />} label="Enquêtes de Satisfaction" />
                                        </Box>
                                        <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                                            <FormControlLabel control={<Checkbox checked={options.errors} onChange={handleOptionChange} name="errors" />} label="Rapports d'Erreurs" />
                                        </Box>
                                    </Box>
                                </FormGroup>
                            </CardContent>
                        </Card>
                    </Fade>
                </Box>

                {/* Recipient & Actions Card */}
                <Box sx={{ flex: { xs: '1 1 100%', md: '0 1 33.333%' } }}>
                    <Fade in timeout={700}>
                        <Card sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <MailIcon color="primary" /> Destinataires
                                </Typography>

                                <FormGroup sx={{ mb: 3 }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={recipients.learner} onChange={handleRecipientChange} name="learner" />}
                                        label={
                                            <Typography variant="body2">
                                                Apprenant <Typography component="span" variant="caption" color="text.secondary">({learner.email})</Typography>
                                            </Typography>
                                        }
                                        sx={{ mb: 1 }}
                                    />
                                    <FormControlLabel
                                        control={<Checkbox checked={recipients.manager} onChange={handleRecipientChange} name="manager" />}
                                        label={
                                            <Typography variant="body2">
                                                Responsable Pédagogique <Typography component="span" variant="caption" color="text.secondary">({currentUser?.firstName} {currentUser?.lastName})</Typography>
                                            </Typography>
                                        }
                                        sx={{ mb: 1 }}
                                    />
                                    <FormControlLabel
                                        control={<Checkbox checked={showOtherInput} onChange={handleRecipientChange} name="otherCheck" />}
                                        label="Tiers (Autre)"
                                    />
                                </FormGroup>

                                {showOtherInput && (
                                    <Fade in>
                                        <TextField
                                            fullWidth
                                            label="Email du tiers"
                                            placeholder="exemple@email.com"
                                            value={recipients.other}
                                            onChange={handleOtherEmailChange}
                                            size="small"
                                            sx={{ mb: 3 }}
                                        />
                                    </Fade>
                                )}

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={generating ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                                        onClick={handleGeneratePDF}
                                        disabled={generating}
                                        sx={{ borderRadius: 2, height: 48 }}
                                    >
                                        {generating ? 'Génération...' : 'Télécharger PDF'}
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                        onClick={handleGenerateAndSend}
                                        disabled={generating || sending}
                                        sx={{
                                            height: 48,
                                            borderRadius: 2,
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                                            '&:hover': {
                                                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                                            }
                                        }}
                                    >
                                        {sending ? 'Envoi...' : 'Générer et Envoyer'}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Fade>
                </Box>
            </Box>
        </Box>
    );
};

export default ReportConfig;
